/**
 * Root-Cause Analysis Wizard
 *
 * Multi-step wizard for root-cause analysis that operates independently of global filters.
 *
 * Step 1: Object Type Selection - Select which object types to analyze
 * Step 2: Attribute Selection - Select which attributes to analyze
 * Step 3: Filter & Statistics - Apply local filters and view attribute statistics
 * Step 4: Root Cause Analysis - Perform Chi-squared test and Odds Ratio analysis
 */

import React, { useState } from 'react';
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent from '../../../components/Tile/TileContent';
import Step1_ObjectTypeSelection from './Step1_ObjectTypeSelection';
import Step2_AttributeSelection from './Step2_AttributeSelection';
import Step3_FilterAndStats from './Step3_FilterAndStats';
import Step4_RootCauseResults from './Step4_RootCauseResults';

// Step height configuration for dynamic sizing
const STEP_MIN_HEIGHTS = {
  1: '400px',   // Minimum for small content (few object types)
  2: '600px',   // Minimum for attribute selection table
  3: '800px',   // Minimum for filters
  4: '500px'    // Minimum for results
};

const STEP_MAX_HEIGHTS = {
  1: '900px',   // Maximum before scrolling (many object types)
  2: '1200px',   // Maximum for attribute table
  3: '1500px',  // Maximum for filter panels
  4: '800px'    // Maximum for results
};

const RootCauseWizard = ({ activeFile }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedObjectTypes, setSelectedObjectTypes] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [dateRangeFilter, setDateRangeFilter] = useState(null);
  const [edgeDurationFilters, setEdgeDurationFilters] = useState([]);
  const [edgeContainmentFilters, setEdgeContainmentFilters] = useState(null);
  const [activityNodeFilters, setActivityNodeFilters] = useState(null);
  const [eventAttributeFilters, setEventAttributeFilters] = useState(null);
  const [objectAttributeFilters, setObjectAttributeFilters] = useState(null);
  const [rootCauseResults, setRootCauseResults] = useState(null);
  const [step3Statistics, setStep3Statistics] = useState(null);
  const [step3ExpandedSections, setStep3ExpandedSections] = useState({
    dateRange: false, edgeFilters: false, activityNodeFilters: false, eventAttributeFilters: false, objectAttributeFilters: false,
  });


  // Handle Step 1 completion (Object Type Selection)
  const handleStep1Next = (selectionData) => {
    console.log('Step 1 complete:', selectionData);
    setSelectedObjectTypes(selectionData.selectedObjectTypes);
    setCurrentStep(2);
  };

  // Handle Step 2 completion (Attribute Selection)
  const handleStep2Next = (selectionData) => {
    console.log('Step 2 complete:', selectionData);
    setSelectedAttributes(selectionData.selectedAttributes);
    setCurrentStep(3);
  };

  // Handle Step 3 completion (Filter & Statistics)
  const handleStep3Next = (filterData) => {
    console.log('Step 3 complete, proceeding to Step 4');
    setDateRangeFilter(filterData.dateRange);
    setEdgeDurationFilters(filterData.edgeDurationFilters || []);
    setEdgeContainmentFilters(filterData.edgeContainmentFilters || null);
    setActivityNodeFilters(filterData.activityNodeFilters || null);
    setEventAttributeFilters(filterData.eventAttributeFilters || null);
    setObjectAttributeFilters(filterData.objectAttributeFilters || null);
    setRootCauseResults(filterData.rootCauseResults || null);
    setStep3Statistics(filterData.statistics || null);
    setStep3ExpandedSections(filterData.expandedSections || { dateRange: false, edgeFilters: false, activityNodeFilters: false, eventAttributeFilters: false, objectAttributeFilters: false });
    setCurrentStep(4);
  };

  // Handle back to Step 1
  const handleBackToStep1 = () => {
    setCurrentStep(1);
  };

  // Handle back to Step 2 — receive and save Step 3 state before leaving
  const handleBackToStep2 = (step3State) => {
    if (step3State) {
      setDateRangeFilter(step3State.dateRange || null);
      setEdgeDurationFilters(step3State.edgeDurationFilters || []);
      setEdgeContainmentFilters(step3State.edgeContainmentFilters || null);
      setActivityNodeFilters(step3State.activityNodeFilters || null);
      setEventAttributeFilters(step3State.eventAttributeFilters || null);
      setObjectAttributeFilters(step3State.objectAttributeFilters || null);
      setStep3Statistics(step3State.statistics || null);
      setStep3ExpandedSections(step3State.expandedSections || { dateRange: false, edgeFilters: false, activityNodeFilters: false, eventAttributeFilters: false, objectAttributeFilters: false });
    }
    setCurrentStep(2);
  };

  // Handle back to Step 3
  const handleBackToStep3 = () => {
    setCurrentStep(3);
  };

  // Handle reset
  const handleReset = () => {
    setCurrentStep(1);
    setSelectedObjectTypes([]);
    setSelectedAttributes([]);
    setDateRangeFilter(null);
    setEdgeDurationFilters([]);
    setEdgeContainmentFilters(null);
    setActivityNodeFilters(null);
    setEventAttributeFilters(null);
    setObjectAttributeFilters(null);
    setRootCauseResults(null);
    setStep3Statistics(null);
    setStep3ExpandedSections({ dateRange: false, edgeFilters: false, activityNodeFilters: false, eventAttributeFilters: false, objectAttributeFilters: false });
  };

  return (
    <BaseTile minHeight="auto" className="h-full">
      <TileContent>
        <div
          className="w-full border border-gray-300 rounded overflow-hidden bg-white relative"
          style={{
            minHeight: STEP_MIN_HEIGHTS[currentStep],
            maxHeight: STEP_MAX_HEIGHTS[currentStep],
            height: 'auto'
          }}
        >
          {/* Step content with internal scrolling */}
          <div className="h-full overflow-y-auto">
            {/* Wizard Header - Fixed at top */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-200 bg-white sticky top-0 z-20">
              <h1 className="text-2xl font-bold text-gray-800">Root-Cause Analysis</h1>
              <p className="text-sm text-gray-600 mt-1">
                Multi-step wizard for analyzing attribute patterns
              </p>

              {/* Step Indicator */}
              <div className="flex items-center mt-4 space-x-2">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  1
                </div>
                <div className="flex-1 h-1 bg-gray-300">
                  <div
                    className={`h-full transition-all ${
                      currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    style={{ width: currentStep >= 2 ? '100%' : '0%' }}
                  />
                </div>
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  2
                </div>
                <div className="flex-1 h-1 bg-gray-300">
                  <div
                    className={`h-full transition-all ${
                      currentStep >= 3 ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    style={{ width: currentStep >= 3 ? '100%' : '0%' }}
                  />
                </div>
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  3
                </div>
                <div className="flex-1 h-1 bg-gray-300">
                  <div
                    className={`h-full transition-all ${
                      currentStep >= 4 ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    style={{ width: currentStep >= 4 ? '100%' : '0%' }}
                  />
                </div>
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    currentStep >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  4
                </div>
              </div>

              <div className="flex items-center space-x-2 text-xs text-gray-600 mt-2">
                {/* 1st step */}
                <span className={`flex items-center justify-center w-8 whitespace-nowrap ${currentStep >= 1 ? 'font-medium text-blue-600' : ''}`}>
                  Object Types
                </span>

                {/* Spacer 1 */}
                <div className="flex-1 h-1 bg-transparent" />

                {/* 2nd step */}
                <span className={`flex items-center justify-center w-8 whitespace-nowrap ${currentStep >= 2 ? 'font-medium text-blue-600' : ''}`}>
                  Attributes
                </span>

                {/* Spacer 2 */}
                <div className="flex-1 h-1 bg-transparent" />

                {/* 3rd step */}
                <span className={`flex items-center justify-center w-8 whitespace-nowrap ${currentStep >= 3 ? 'font-medium text-blue-600' : ''}`}>
                  Filter & Stats
                </span>

                {/* Spacer 3 */}
                <div className="flex-1 h-1 bg-transparent" />

                {/* 4th step */}
                <span className={`flex items-center justify-center w-8 whitespace-nowrap ${currentStep >= 4 ? 'font-medium text-blue-600' : ''}`}>
                  Root Cause
                </span>
              </div>
            </div>

            {/* Step Content Area */}
            {currentStep === 1 && (
              <Step1_ObjectTypeSelection
                activeFile={activeFile}
                initialSelectedObjectTypes={selectedObjectTypes}
                onNext={handleStep1Next}
              />
            )}

            {currentStep === 2 && (
              <Step2_AttributeSelection
                activeFile={activeFile}
                selectedObjectTypes={selectedObjectTypes}
                initialSelectedAttributes={selectedAttributes}
                onBack={handleBackToStep1}
                onNext={handleStep2Next}
              />
            )}

            {currentStep === 3 && (
              <Step3_FilterAndStats
                activeFile={activeFile}
                selectedObjectTypes={selectedObjectTypes}
                selectedAttributes={selectedAttributes}
                initialDateRange={dateRangeFilter}
                initialEdgeDurationFilters={edgeDurationFilters}
                initialEdgeContainmentFilters={edgeContainmentFilters}
                initialActivityNodeFilters={activityNodeFilters}
                initialEventAttributeFilters={eventAttributeFilters}
                initialObjectAttributeFilters={objectAttributeFilters}
                initialStatistics={step3Statistics}
                initialExpandedSections={step3ExpandedSections}
                onBack={handleBackToStep2}
                onNext={handleStep3Next}
              />
            )}

            {currentStep === 4 && (
              <Step4_RootCauseResults
                activeFile={activeFile}
                selectedObjectTypes={selectedObjectTypes}
                selectedAttributes={selectedAttributes}
                dateRange={dateRangeFilter}
                edgeDurationFilters={edgeDurationFilters}
                edgeContainmentFilters={edgeContainmentFilters}
                activityNodeFilters={activityNodeFilters}
                eventAttributeFilters={eventAttributeFilters}
                objectAttributeFilters={objectAttributeFilters}
                initialResults={rootCauseResults}
                onBack={handleBackToStep3}
                onReset={handleReset}
              />
            )}
          </div>
        </div>
      </TileContent>
    </BaseTile>
  );
};

export default RootCauseWizard;
